import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useAuthStore } from '@/stores/auth'

/** Account-scoped acknowledged reads with explicit error/retry and no stale-scope paint.
 * Pass a stable read callback. A failed refresh retains this scope's last data only. */
export function useScopedRead<T>(resource: string | null, read: () => Promise<T>) {
  const userId = useAuthStore((state) => state.user?.id)
  const key = userId && resource ? `${userId}:${resource}` : null
  const empty = (scopeKey: string | null) => ({
    key: scopeKey,
    data: null as T | null,
    reader: read,
    loading: !!scopeKey,
    error: false,
  })
  const [snapshot, setSnapshot] = useState(() => empty(key))
  const sequence = useRef(0)
  const scope = useRef({ key, alive: true })
  useLayoutEffect(() => {
    scope.current = { key, alive: true }
    sequence.current++
  }, [key])
  const load = useCallback(async () => {
    const active = scope.current
    if (!key || !active.alive || active.key !== key) return
    const request = ++sequence.current
    const current = () => active === scope.current && active.alive && request === sequence.current
    // Normalize synchronous adapter throws to the same asynchronous result path.
    // State changes here acknowledge a completed read, never start an effect.
    const pending = (async () => read())()
    await pending.then(
      (data) => {
        if (current()) setSnapshot({ key, reader: read, data, loading: false, error: false })
      },
      () => {
        if (current())
          setSnapshot((previous) => ({
            key,
            reader: read,
            data: previous.key === key ? previous.data : null,
            loading: false,
            error: true,
          }))
      },
    )
  }, [key, read])
  const reload = useCallback(async () => {
    if (!key || scope.current.key !== key || !scope.current.alive) return
    setSnapshot((previous) => ({
      key,
      reader: read,
      data: previous.key === key ? previous.data : null,
      loading: true,
      error: previous.key === key ? previous.error : false,
    }))
    await load()
  }, [key, read, load])
  useEffect(() => {
    const active = scope.current
    active.alive = true
    void load()
    return () => {
      active.alive = false
    }
  }, [load])
  // Optional acknowledged writes share the same account/resource fence as reads.
  // Reconcile only canonical server results; a failed write leaves the snapshot intact.
  const mutate = useCallback(
    async <R>(operation: () => Promise<R>, reconcile: (data: T | null, result: R) => T) => {
      const active = scope.current
      const current = () =>
        !!key &&
        active === scope.current &&
        active.alive &&
        active.key === key &&
        useAuthStore.getState().user?.id === userId
      if (!current()) throw new Error('scoped_resource_changed')
      const result = await operation()
      if (!current()) throw new Error('scoped_resource_changed')
      sequence.current++
      setSnapshot((previous) => {
        const saved = previous.key === key ? previous : null
        return {
          key,
          reader: read,
          data: reconcile(saved?.data ?? null, result),
          loading: false,
          // A single acknowledged mutation does not prove the unread rest of a list.
          error: !saved || saved.data === null || saved.error,
        }
      })
      return result
    },
    [key, userId, read],
  )
  const visible =
    key && snapshot.key === key
      ? snapshot.reader === read
        ? snapshot
        : { ...snapshot, loading: true, error: false }
      : empty(key)
  return { ...visible, reload, mutate }
}
